import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { UNSAFE_NavigationContext as NavigationContext, useNavigate } from 'react-router-dom';
import { useSpreadElements } from '../../hooks/useSpreadElements.js';
import SpreadToolbar from './components/SpreadToolbar';
import SpreadCanvas from './components/canvas/SpreadCanvas';
import DieLinesOverlay from './components/DieLinesOverlay';
import SelectedElementControlPanel from './components/SelectedElementControlPanel';
import LeftControlPanel from './components/LeftControlPanel';
import ElementContextMenu from './components/ElementContextMenu';
import { getSpreadDimensions } from './constants';
import { useBook } from '../../contexts/BookContext';
import { useDimensions } from '../../contexts/DimensionContext';
import { DocumentTextIcon, PhotographIcon, TemplateIcon } from './icons.jsx';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  moveElementBackward,
  moveElementForward,
  moveElementToFront,
  moveElementToBack,
  isElementAtTop,
  isElementAtBottom,
} from './utils/zIndexHelpers';

// Helper to remove undefined values from an object recursively
const sanitizeData = (obj) => {
  if (!obj) return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeData(item));
  }
  
  if (typeof obj !== 'object') return obj;
  
  const result = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      result[key] = sanitizeData(obj[key]);
    }
  }
  return result;
};

function useBlocker(shouldBlock) {
  const { navigator } = useContext(NavigationContext);
  const anyNavigator = /** @type {any} */ (navigator);
  const [state, setState] = useState('unblocked');
  const txRef = useRef(null);

  useEffect(() => {
    if (!anyNavigator?.block) return;

    const unblock = anyNavigator.block((tx) => {
      const block = shouldBlock(tx);
      if (!block) {
        tx.retry();
        return;
      }

      txRef.current = tx;
      setState('blocked');
    });

    return unblock;
  }, [anyNavigator, shouldBlock]);

  const proceed = useCallback(() => {
    const tx = txRef.current;
    txRef.current = null;
    setState('unblocked');
    tx?.retry?.();
  }, []);

  const reset = useCallback(() => {
    txRef.current = null;
    setState('unblocked');
  }, []);

  return { state, proceed, reset };
}

export default function BookSpread({
  bookId,
  totalPages,
  pageSize = 'us-trade',
  ensureOverlay,
  initialSpreadId = 0,
}) {
  const [selectedElement, setSelectedElement] = useState(null);
  const [currentSpread, setCurrentSpread] = useState(initialSpreadId);
  const [showDieLines, setShowDieLines] = useState(false);
  const [paperColor, setPaperColor] = useState('#FFFFFF');
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [showBorderEditor, setShowBorderEditor] = useState(false);
  const [contextMenu, setContextMenu] = useState({ isOpen: false, x: 0, y: 0, elementId: null });
  const [generatingElementId, setGeneratingElementId] = useState(null);
  
  // IMPORTANT: We prioritize the page size passed as a prop over the context
  // This ensures the BookSpread component respects the selected page size directly
  const { selectedPageSize } = useDimensions();
  const effectivePageSize = pageSize || selectedPageSize;
  
  // Get dimensions based on the actual page size and log it prominently
  const dimensions = getSpreadDimensions(effectivePageSize);
  
  // Set a visible indicator of the page size in the console
  console.log(`%c ACTIVE PAGE SIZE: ${effectivePageSize.toUpperCase()} `, 'background: #3b82f6; color: white; font-weight: bold;');
  console.log('Dimensions:', dimensions);
  
  // Log when BookSpread re-renders with page size
  useEffect(() => {
    console.log(`BookSpread updated with page size: ${effectivePageSize}`);
  }, [effectivePageSize]);
  
  const { currentBook, updateBook, setActiveSpread } = useBook();

  // Track the active spread so BookContext skips onSnapshot merges for it
  useEffect(() => {
    const sid = `spread-${currentSpread}`;
    setActiveSpread(sid);
    return () => setActiveSpread(null);
  }, [currentSpread, setActiveSpread]);

  const SpreadCanvasComponent = /** @type {any} */ (SpreadCanvas);

  const spreadState = /** @type {any} */ (
    useSpreadElements(bookId, `spread-${currentSpread}`)
  );

  const {
    elements,
    isLoading,
    error,
    isDirty,
    isSaving,
    loadElements,
    updateElements,
    saveElements,
  } = spreadState;
  
  // Debug: log what's in currentBook.spreads for this spread
  useEffect(() => {
    const spreadId = `spread-${currentSpread}`;
    const spreadData = currentBook?.spreads?.[spreadId];
    console.log('[BookSpread] Current spread data:', {
      spreadId,
      hasSpreadData: !!spreadData,
      elementCount: spreadData?.elements?.length || 0,
      elements: spreadData?.elements?.map(el => ({
        id: el.id,
        type: el.type,
        orientation: el.orientation,
      })) || []
    });
  }, [currentBook?.spreads, currentSpread]);

  const navigationBlocker = useBlocker((tx) => {
    if (!isDirty) return false;
    return tx.location?.pathname && window.location.pathname !== tx.location.pathname;
  });

  useEffect(() => {
    if (navigationBlocker.state === 'blocked') {
      setShowUnsavedChangesModal(true);
    }
  }, [navigationBlocker.state]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__sbwDesignIsDirty = !!isDirty;
    window.__sbwDesignSave = async () => {
      try {
        await saveElements();
        return true;
      } catch (e) {
        console.error('Design save failed', e);
        return false;
      }
    };
    return () => {
      window.__sbwDesignIsDirty = false;
      window.__sbwDesignSave = undefined;
    };
  }, [isDirty, saveElements]);

  // Load elements when spread changes
  useEffect(() => {
    console.log('DEBUG BookSpread: Loading elements for spread:', {
      bookId,
      spreadId: `spread-${currentSpread}`,
      hasCurrentBook: !!currentBook,
    });

    if (bookId && !isSaving) {
      loadElements();
    }
  }, [bookId, currentSpread, loadElements, isSaving]);

  // Load paper color when spread changes
  useEffect(() => {
    // Only apply cover color for spread-0
    if (currentSpread === 0) {
      const coverColor = currentBook?.coverDetails?.frontCover?.color || '#FFFFFF';
      setPaperColor(coverColor);
      return;
    }

    // Reset to default white for all other spreads
    setPaperColor('#FFFFFF');
  }, [currentBook?.coverDetails?.frontCover?.color, currentSpread]);

  // Handle arrow key positioning for selected element
  useEffect(() => {
    if (!selectedElement) return;

    const handleKeyDown = (e) => {
      const isArrowKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key);
      if (!isArrowKey) return;

      // Don't move element if we're editing text in a textarea
      if (document.activeElement?.tagName === 'TEXTAREA') return;

      // Determine distance based on modifiers
      let distance = 5; // Default: 5px
      if (e.shiftKey) distance = 10; // Shift: 10px
      if (e.ctrlKey || e.metaKey) distance = 1; // Ctrl/Cmd: 1px

      const currentElement = elements.find((el) => el.id === selectedElement);
      if (!currentElement) return;

      const currentX = currentElement.position?.x || 0;
      const currentY = currentElement.position?.y || 0;
      let newX = currentX;
      let newY = currentY;

      switch (e.key) {
        case 'ArrowUp':
          newY = Math.max(0, currentY - distance);
          e.preventDefault();
          break;
        case 'ArrowDown':
          newY = currentY + distance;
          e.preventDefault();
          break;
        case 'ArrowLeft':
          newX = Math.max(0, currentX - distance);
          e.preventDefault();
          break;
        case 'ArrowRight':
          newX = currentX + distance;
          e.preventDefault();
          break;
        default:
          break;
      }

      const updatedElements = elements.map((el) => {
        if (el.id === selectedElement) {
          return {
            ...el,
            position: {
              ...el.position,
              x: newX,
              y: newY,
            },
          };
        }
        return el;
      });

      updateElements(updatedElements);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement, elements, updateElements]);

  // Define getUnusedImagePrompt first since it's used by handleAddElement
  const getUnusedImagePrompt = useCallback(() => {
    console.log('DEBUG BookSpread: Getting unused image prompt:', {
      currentSpread,
      hasBookElements: !!currentBook?.elements,
      totalElements: currentBook?.elements?.length,
    });

    if (!currentBook?.elements) {
      console.log('DEBUG BookSpread: No book elements found');
      return '';
    }

    // Get all image prompts for the current spread from the book data
    const spreadImageElements = currentBook.elements.filter(
      (el) =>
        el.spreadId === `spread-${currentSpread}` &&
        el.type === 'image' &&
        el.imagePrompt
    );

    console.log('DEBUG BookSpread: Found spread image elements:', {
      spreadId: `spread-${currentSpread}`,
      elements: spreadImageElements.map((el) => ({
        id: el.id,
        imagePrompt: el.imagePrompt,
      })),
    });

    // Find a prompt that hasn't been used in the current spread's elements
    const unusedPrompt = spreadImageElements.find(
      (bookEl) => !elements.some((el) => el.imagePrompt === bookEl.imagePrompt)
    );

    if (unusedPrompt) {
      console.log(
        'DEBUG BookSpread: Found unused prompt:',
        unusedPrompt.imagePrompt
      );
      return unusedPrompt.imagePrompt;
    }

    console.log('DEBUG BookSpread: No unused image prompts found');
    return '';
  }, [currentBook, currentSpread, elements]);

  const navigate = useNavigate();

  const handleSpreadChange = useCallback(
    async (direction) => {
      console.log('DEBUG BookSpread: Changing spread:', {
        direction,
        currentSpread,
        isDirty,
      });

      // Check if there are any elements with lastGenerationFailed: true
      const failedElements = elements.filter(el => el.lastGenerationFailed);

      if (failedElements.length > 0) {
        console.log('[BookSpread] Found failed elements, setting lastGenerationFailed to false:', { count: failedElements.length });
        
        // Set lastGenerationFailed to false locally (just like on hover)
        const updatedElements = elements.map(el => {
          if (el.lastGenerationFailed) {
            return { ...el, lastGenerationFailed: false };
          }
          return el;
        });
        
        // Update locally
        updateElements(updatedElements);
        
        // Directly save to Firestore without waiting for state updates
        try {
          const spreadId = `spread-${currentSpread}`;
          const spreadRef = doc(db, 'books', bookId, 'spreads', spreadId);
          const bookRef = doc(db, 'books', bookId);
          
          console.log('[BookSpread] About to save to Firestore:', {
            spreadId,
            bookId,
            elementCount: updatedElements.length,
            updatedElements: updatedElements.map(el => ({
              id: el.id,
              lastGenerationFailed: el.lastGenerationFailed
            }))
          });
          
          // Sanitize elements to remove undefined values
          const sanitizedElements = sanitizeData(updatedElements);
          
          // Update the spreads subcollection
          const result1 = await updateDoc(spreadRef, {
            elements: sanitizedElements,
            updatedAt: serverTimestamp()
          });
          console.log('[BookSpread] Spreads subcollection updated:', result1);
          
          // Also update the nested spreads field on the main book document
          const fieldPath = `spreads.${spreadId}.elements`;
          const result2 = await updateDoc(bookRef, {
            [fieldPath]: sanitizedElements,
            updatedAt: serverTimestamp()
          });
          console.log('[BookSpread] Main book document updated:', result2);
          
          console.log('[BookSpread] ✅ Elements saved to Firestore successfully!');
          
          // Now navigate to the next/previous spread
          const maxSpreads = Math.ceil(totalPages / 2);
          const newSpread =
            direction === 'next'
              ? Math.min(currentSpread + 1, maxSpreads)
              : Math.max(0, currentSpread - 1);

          setCurrentSpread(newSpread);
          setSelectedElement(null);
          navigate(`/design/${bookId}/${newSpread}`, { replace: true });
        } catch (err) {
          console.error('[BookSpread] ❌ Error saving elements:', err);
        }
        
        return;
      }

      if (isDirty) {
        await saveElements();
      }

      const maxSpreads = Math.ceil(totalPages / 2);
      const newSpread =
        direction === 'next'
          ? Math.min(currentSpread + 1, maxSpreads)
          : Math.max(0, currentSpread - 1);

      setCurrentSpread(newSpread);
      setSelectedElement(null);
      navigate(`/design/${bookId}/${newSpread}`, { replace: true });
    },
    [currentSpread, totalPages, isDirty, saveElements, bookId, navigate, elements, updateElements]
  );

  const handleElementsChange = useCallback(
    (elementId, updates) => {
      // Find the element and apply updates
      const elementIndex = elements.findIndex(el => el.id === elementId);
      
      if (elementIndex !== -1) {
        // Create a new array with the updated element
        const newElements = [...elements];
        newElements[elementIndex] = {
          ...elements[elementIndex],
          ...updates
        };
        
        // Update local state only - Firestore save happens when user clicks "Save Changes"
        updateElements(newElements);
      }
    },
    [elements, updateElements]
  );

  // Handle z-index change
  const handleZIndexChange = useCallback(
    (elementId, newZIndex) => {
      console.log('[SIMPLE] handleZIndexChange called with:', elementId, newZIndex);
      
      const elementIndex = elements.findIndex(el => el.id === elementId);
      
      if (elementIndex !== -1) {
        // Create a new array with the updated element
        const newElements = [...elements];
        newElements[elementIndex] = {
          ...elements[elementIndex],
          zIndex: newZIndex
        };
        
        updateElements(newElements);
      }
    },
    [elements, updateElements]
  );

  // Context menu handlers
  const handleContextMenuShow = useCallback(
    (elementId, x, y) => {
      setContextMenu({ isOpen: true, x, y, elementId });
    },
    []
  );

  const handleContextMenuClose = useCallback(() => {
    setContextMenu({ isOpen: false, x: 0, y: 0, elementId: null });
  }, []);

  const handleMoveForward = useCallback(() => {
    if (!contextMenu.elementId) return;
    const updatedElements = moveElementForward(elements, contextMenu.elementId);
    updateElements(updatedElements);
  }, [contextMenu.elementId, elements, updateElements]);

  const handleMoveBackward = useCallback(() => {
    if (!contextMenu.elementId) return;
    const updatedElements = moveElementBackward(elements, contextMenu.elementId);
    updateElements(updatedElements);
  }, [contextMenu.elementId, elements, updateElements]);

  const handleMoveToFront = useCallback(() => {
    if (!contextMenu.elementId) return;
    const updatedElements = moveElementToFront(elements, contextMenu.elementId);
    updateElements(updatedElements);
  }, [contextMenu.elementId, elements, updateElements]);

  const handleMoveToBack = useCallback(() => {
    if (!contextMenu.elementId) return;
    const updatedElements = moveElementToBack(elements, contextMenu.elementId);
    updateElements(updatedElements);
  }, [contextMenu.elementId, elements, updateElements]);

  // Setup window callback for context menu
  useEffect(() => {
    window.__sbwShowContextMenu = handleContextMenuShow;
    return () => {
      window.__sbwShowContextMenu = undefined;
    };
  }, [handleContextMenuShow]);

  // Setup window callback to close context menu
  useEffect(() => {
    window.__sbwCloseContextMenu = handleContextMenuClose;
    return () => {
      window.__sbwCloseContextMenu = undefined;
    };
  }, [handleContextMenuClose]);

  // Handle arrow key movement for selected element
  const handleArrowKeyMove = useCallback(
    (e) => {
      if (!selectedElement) return;
      
      const moveDistance = 10; // pixels per arrow key press
      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      
      if (!arrowKeys.includes(e.key)) return;

      // Don't move element if we're editing text in a textarea
      if (document.activeElement?.tagName === 'TEXTAREA') return;
      
      e.preventDefault();
      
      const elementIndex = elements.findIndex(el => el.id === selectedElement);
      if (elementIndex === -1) return;
      
      const element = elements[elementIndex];
      let newX = element.position.x;
      let newY = element.position.y;
      
      switch (e.key) {
        case 'ArrowUp':
          newY = Math.max(0, newY - moveDistance);
          break;
        case 'ArrowDown':
          newY = newY + moveDistance;
          break;
        case 'ArrowLeft':
          newX = Math.max(0, newX - moveDistance);
          break;
        case 'ArrowRight':
          newX = newX + moveDistance;
          break;
      }
      
      const newElements = [...elements];
      newElements[elementIndex] = {
        ...element,
        position: { x: newX, y: newY }
      };
      
      updateElements(newElements);
    },
    [selectedElement, elements, updateElements]
  );

  // Add keyboard event listener for arrow keys
  useEffect(() => {
    if (selectedElement) {
      window.addEventListener('keydown', handleArrowKeyMove);
      return () => {
        window.removeEventListener('keydown', handleArrowKeyMove);
      };
    }
  }, [selectedElement, handleArrowKeyMove]);

  const handleAddElement = useCallback(
    (type) => {
      console.log('DEBUG BookSpread: Adding new element:', {
        type,
        spreadId: `spread-${currentSpread}`,
        currentElementCount: elements.length,
      });

      let imagePrompt = '';
      if (type === 'image') {
        imagePrompt = getUnusedImagePrompt();
        console.log('DEBUG BookSpread: Got image prompt:', imagePrompt);
      }

      // Find highest zIndex to place new element on top
      const highestZIndex = elements.length > 0
        ? Math.max(...elements.map((el) => parseInt(el.zIndex || 0)))
      : 0;
      
      const newZIndex = highestZIndex + 1;
      console.log('[SIMPLE] Creating new element with z-index:', newZIndex, 'highest found:', highestZIndex);

      const initialPos = { x: 50, y: 50 };
      const newElement = {
        id: crypto.randomUUID(),
        type,
        spreadId: `spread-${currentSpread}`,
        position: initialPos,
        initialPosition: initialPos,
        size: {
          width: type === 'image' ? 500 : (type === 'shape' ? 150 : 200),
          height: type === 'image' ? 800 : (type === 'shape' ? 150 : 100),
        },
        style: type === 'shape' ? { fill: '#ff0000', opacity: 1, borderRadius: '0px' } : {},
        zIndex: newZIndex,
        content: type === 'text' ? 'Click to edit' : '',
        imagePrompt: type === 'image' ? imagePrompt : undefined,
        shape: type === 'shape' ? 'square' : undefined,
      };

      console.log('[SIMPLE] Created new element:', {
        id: newElement.id.substring(0, 8),
        type: newElement.type,
        zIndex: newElement.zIndex
      });

      console.log('DEBUG BookSpread: Created new element:', newElement);

      updateElements([...elements, newElement]);
      setSelectedElement(newElement.id);

      // Auto-open the editor for newly added image elements
      if (type === 'image') {
        setTimeout(() => {
          try {
            if (typeof window !== 'undefined') {
              // Provide a fallback for components that check a global id
              window['__sbwEditRequestId'] = newElement.id;
              window.dispatchEvent(
                new CustomEvent('sbw:request-edit', { detail: { id: newElement.id } })
              );
            }
          } catch {}
        }, 50);
      }
    },
    [
      elements,
      currentSpread,
      getUnusedImagePrompt,
      updateElements,
      setSelectedElement,
    ]
  );

  // Handle paper color change
  const handlePaperColorChange = useCallback(
    (newColor) => {
      setPaperColor(newColor);
      // Update the book's cover color
      if (currentBook && currentSpread === 0) {
        updateBook(bookId, {
          coverDetails: {
            ...currentBook.coverDetails,
            frontCover: {
              ...currentBook.coverDetails?.frontCover,
              color: newColor,
            },
          },
        });
      }
    },
    [currentBook, currentSpread, bookId, updateBook]
  );

  const handleTogglePageNumbers = useCallback(
    async () => {
      if (currentBook) {
        const newHasPageNumbers = !currentBook.hasPageNumbers;
        
        // Import toast dynamically to avoid issues
        const { toast } = await import('react-toastify');
        
        // Show toast notification
        if (newHasPageNumbers) {
          toast.success('Page numbers enabled', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          toast.info('Page numbers disabled', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
        
        await updateBook(bookId, {
          hasPageNumbers: newHasPageNumbers,
        });
      }
    },
    [currentBook, bookId, updateBook]
  );

  // Debug elements state
  useEffect(() => {
    console.log('DEBUG BookSpread: Elements state changed:', {
      spreadId: `spread-${currentSpread}`,
      elementCount: elements.length,
      elements: elements.map((el) => ({
        id: el.id,
        type: el.type,
        content: el.type === 'text' ? el.content : undefined,
        position: el.position,
        zIndex: el.zIndex,
        ...(el.type === 'image'
          ? {
              imagePrompt: el.imagePrompt,
              src: el.src,
            }
          : {}),
      })),
    });
  }, [elements, currentSpread]);

  // Debug when saving state changes
  useEffect(() => {
    console.log('DEBUG BookSpread: Save state changed:', {
      isDirty,
      isSaving,
      error: error || 'none',
    });
  }, [isDirty, isSaving, error]);

  // Add a ref for the SpreadCanvas component
  const spreadCanvasRef = useRef(null);

  // Listen for external requests to navigate to a specific spread
  useEffect(() => {
    const handler = (e) => {
      try {
        const d = e?.detail || {};
        let tgt = d.spreadNumber;
        const sid = d.spreadId || d.spread || d.id;
        if (tgt == null && sid) {
          const m = String(sid).match(/spread-(\d+)/);
          if (m) tgt = parseInt(m[1], 10);
        }
        if (typeof tgt !== 'number' || !isFinite(tgt)) return;
        const maxSpreads = Math.ceil(totalPages / 2);
        const newSpread = Math.max(0, Math.min(tgt, maxSpreads));
        (async () => {
          try { if (isDirty) await saveElements(); } catch {}
          setCurrentSpread(newSpread);
          setSelectedElement(null);
        })();
      } catch {}
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('sbw:go-to-spread', handler);
      return () => window.removeEventListener('sbw:go-to-spread', handler);
    }
  }, [totalPages, isDirty, saveElements]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 p-4">Error loading spread: {error}</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-center mb-4">
          {/* Z-Index Controls removed from here */}
        </div>

        <SpreadToolbar
          onAddElement={handleAddElement}
          onSave={saveElements}
          isDirty={isDirty}
          isSaving={isSaving}
          currentSpread={currentSpread}
          totalPages={totalPages}
          onPreviousSpread={() => handleSpreadChange('prev')}
          onNextSpread={() => handleSpreadChange('next')}
          showDieLines={showDieLines}
          onToggleDieLines={() => setShowDieLines(!showDieLines)}
          paperColor={paperColor}
          onPaperColorChange={handlePaperColorChange}
          hasPageNumbers={currentBook?.hasPageNumbers || false}
          onTogglePageNumbers={handleTogglePageNumbers}
        />

        <div className="relative mt-6">
          <SpreadCanvasComponent
            ref={spreadCanvasRef}
            elements={elements}
            selectedElement={selectedElement}
            onSelectElement={setSelectedElement}
            onElementsChange={handleElementsChange}
            onElementDelete={(elementId) => {
              const updatedElements = elements.filter(
                (el) => el.id !== elementId
              );
              updateElements(updatedElements);
              setSelectedElement(null);
            }}
            onElementDuplicate={(elementId) => {
              const elementToDuplicate = elements.find(
                (el) => el.id === elementId
              );
              if (elementToDuplicate) {
                // Find highest zIndex to place duplicate on top
                const highestZIndex = elements.length > 0
                  ? Math.max(...elements.map((el) => parseInt(el.zIndex || 0)))
                : 0;

                const newPosition = {
                  x: elementToDuplicate.position.x + 20,
                  y: elementToDuplicate.position.y + 20,
                };
                const newElement = {
                  ...elementToDuplicate,
                  id: crypto.randomUUID(),
                  position: newPosition,
                  initialPosition: newPosition,
                  zIndex: highestZIndex + 1,
                };
                const updatedElements = [...elements, newElement];
                updateElements(updatedElements);
                setSelectedElement(newElement.id);
              }
            }}
            onZIndexChange={handleZIndexChange}
            dimensions={dimensions}
            paperColor={paperColor}
            hasPageNumbers={currentBook?.hasPageNumbers || false}
            currentSpread={currentSpread}
            totalPages={totalPages}
            ensureOverlay={ensureOverlay}
            saveElements={saveElements}
            generatingElementId={generatingElementId}
          />

          <DieLinesOverlay visible={showDieLines} dimensions={dimensions} />
          
          {/* Left Control Panel - Layers (Toggleable) */}
          <LeftControlPanel
            selectedElement={selectedElement}
            elements={elements}
            onSelectElement={setSelectedElement}
            onReorderElements={updateElements}
          />
          
          {/* Right Control Panel - Element Controls */}
           <SelectedElementControlPanel
             selectedElement={selectedElement}
             elements={elements}
             onUpdateElements={updateElements}
             onOpenImageEditor={() => setShowImageEditor(true)}
             onOpenBorderEditor={() => setShowBorderEditor(true)}
             onSaveSpread={saveElements}
             onGeneratingChange={setGeneratingElementId}
             currentBook={currentBook}
             currentSpread={currentSpread}
           />

          {/* Context Menu */}
          <ElementContextMenu
            isOpen={contextMenu.isOpen}
            x={contextMenu.x}
            y={contextMenu.y}
            elementId={contextMenu.elementId}
            elements={elements}
            onClose={handleContextMenuClose}
            onMoveForward={handleMoveForward}
            onMoveBackward={handleMoveBackward}
            onMoveToFront={handleMoveToFront}
            onMoveToBack={handleMoveToBack}
          />
        </div>
      </div>

      {showUnsavedChangesModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setShowUnsavedChangesModal(false);
                navigationBlocker.reset?.();
              }}
            />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Unsaved changes
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      You have unsaved changes. If you leave now, your changes may be lost.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-6 space-y-3">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  onClick={() => {
                    (async () => {
                      await saveElements();
                      setShowUnsavedChangesModal(false);
                      navigationBlocker.proceed?.();
                    })();
                  }}
                >
                  Save and Continue
                </button>
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
                  onClick={() => {
                    setShowUnsavedChangesModal(false);
                    navigationBlocker.proceed?.();
                  }}
                >
                  Continue without Saving Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
